// Chat Widget Component
class ChatWidget {
  constructor() {
    this.isOpen = false;
    this.trustedOrigin = this._resolveTrustedOrigin();
    this.embedUrl = this._resolveEmbedUrl();
    this.logoUrl = this._resolveAssetUrl('/publk_circle_purple.png');
    this._applyGroupParam();
    this.init();
  }

  /**
   * Optional `data-group` on the <script> tag: the embedding site's public
   * user group (must be allowlisted in the backend's PUBLIC_GROUPS). Flows
   * into the iframe URL so the embed uses that group's KB and branding.
   */
  _applyGroupParam() {
    const scriptTag = this._findWidgetScriptTag();
    const group = scriptTag && scriptTag.getAttribute('data-group');
    if (!group) return;
    if (!/^[a-z][a-z0-9_]{1,31}$/.test(group)) {
      console.warn('Widget: Ignoring invalid data-group value');
      return;
    }
    try {
      const url = new URL(this.embedUrl);
      url.searchParams.set('group', group);
      this.embedUrl = url.toString();
    } catch (_) { /* keep embedUrl unchanged */ }
  }

  _findWidgetScriptTag() {
    const scriptTags = document.querySelectorAll('script[src*="widget.js"]');
    return scriptTags.length > 0 ? scriptTags[scriptTags.length - 1] : null;
  }

  /**
   * Determine the trusted iframe origin.
   * 1. Explicit `data-chatbot-origin` attribute on the <script> tag.
   * 2. Derived from the script's own `src` URL (works when widget.js is
   *    loaded cross-origin from the chatbot domain).
   * 3. Fallback: current page origin (same-origin dev/demo scenarios).
   */
  _resolveTrustedOrigin() {
    const scriptTag = this._findWidgetScriptTag();
    if (scriptTag) {
      const dataOrigin = scriptTag.getAttribute('data-chatbot-origin');
      if (dataOrigin) {
        // Require absolute origin override to avoid ambiguous relative values.
        if (!/^https?:\/\//i.test(dataOrigin)) {
          console.warn(
            'Widget: Ignoring non-absolute data-chatbot-origin. Use full origin like https://chatbot.example.com'
          );
        } else {
          try {
            return new URL(dataOrigin).origin;
          } catch (_) { /* fall through */ }
        }
      }

      try {
        const scriptUrl = new URL(scriptTag.src, window.location.href);
        return scriptUrl.origin;
      } catch (_) { /* fall through */ }
    }
    return window.location.origin;
  }

  /**
   * Determine iframe URL.
   * 1. Explicit `data-chatbot-embed-url` on the <script> tag.
   * 2. Default to `<trustedOrigin>/embed.html`.
   */
  _resolveEmbedUrl() {
    const scriptTag = this._findWidgetScriptTag();
    if (scriptTag) {
      const dataEmbedUrl = scriptTag.getAttribute('data-chatbot-embed-url');
      if (dataEmbedUrl) {
        try {
          // Relative overrides resolve against trustedOrigin to avoid host-page leakage.
          const resolved = new URL(dataEmbedUrl, this.trustedOrigin);
          // Enforce same-origin with trustedOrigin to fail closed on misconfiguration.
          if (resolved.origin !== this.trustedOrigin) {
            console.warn(
              'Widget: Ignoring data-chatbot-embed-url with different origin than trusted origin'
            );
          } else {
            return resolved.toString();
          }
        } catch (_) { /* fall through */ }
      }
    }
    return new URL('/embed.html', this.trustedOrigin).toString();
  }

  _resolveAssetUrl(assetPath) {
    try {
      return new URL(assetPath, this.trustedOrigin).toString();
    } catch (_) {
      return assetPath;
    }
  }

  init() {
    // Create widget HTML
    const widgetHTML = `
      <div id="chatWidget" class="chat-widget">
        <button id="chatWidgetToggle" class="chat-widget-toggle" aria-label="Open chat">
          <img src="${this.logoUrl}" alt="Chat" class="widget-logo widget-logo-chat" />
          <svg class="widget-icon widget-icon-chat-overlay" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z" fill="white"/>
            <path d="M7 9H17M7 13H14" stroke="#ff046b" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <svg class="widget-icon widget-icon-close" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <div id="chatWidgetWindow" class="chat-widget-window">
          <iframe
            src="${this.embedUrl}"
            frameborder="0"
            allow="clipboard-read; clipboard-write"
            class="chat-widget-iframe"
          ></iframe>
        </div>
      </div>
    `;

    // Add widget to page
    document.body.insertAdjacentHTML('beforeend', widgetHTML);

    // Add styles
    this.addStyles();

    // Add event listeners
    this.setupEventListeners();
  }

  addStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .chat-widget {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      }

      .chat-widget-toggle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: #ff046b;
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6d3c8b;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;
      }

      .chat-widget-toggle:hover {
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(109, 60, 139, 0.3);
      }

      .chat-widget-toggle:active {
        transform: scale(0.95);
      }

      .widget-icon,
      .widget-logo {
        position: absolute;
        transition: all 0.3s ease;
      }

      .widget-logo-chat {
        width: 100%;
        height: 100%;
        object-fit: contain;
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }

      .widget-icon-chat-overlay {
        opacity: 1;
        transform: scale(1);
        z-index: 1;
      }

      .widget-icon-close {
        opacity: 0;
        transform: rotate(180deg) scale(0);
      }

      .chat-widget-toggle.active .widget-icon-chat-overlay {
        opacity: 0;
        transform: rotate(-180deg) scale(0);
      }

      .chat-widget-toggle.active .widget-logo-chat {
        opacity: 0;
        transform: rotate(-180deg) scale(0);
      }

      .chat-widget-toggle.active .widget-icon-close {
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }

      .chat-widget-window {
        position: absolute;
        bottom: 80px;
        right: 0;
        width: 380px;
        height: 600px;
        max-height: calc(100vh - 120px);
        background: white;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        opacity: 0;
        visibility: hidden;
        transform: translateY(20px) scale(0.95);
        transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        overflow: hidden;
      }

      .chat-widget-window.open {
        opacity: 1;
        visibility: visible;
        transform: translateY(0) scale(1);
      }

      .chat-widget-iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 16px;
      }

      /* Mobile responsiveness */
      @media (max-width: 480px) {
        .chat-widget {
          bottom: 10px;
          right: 10px;
        }

        .chat-widget-window {
          width: calc(100vw - 20px);
          height: calc(100vh - 100px);
          max-height: calc(100vh - 100px);
          bottom: 70px;
          right: -10px;
        }

        .chat-widget-toggle {
          width: 56px;
          height: 56px;
        }
      }

      /* Animation for pulse effect */
      @keyframes pulse {
        0% {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(109, 60, 139, 0.4);
        }
        70% {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 10px rgba(109, 60, 139, 0);
        }
        100% {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 0 rgba(109, 60, 139, 0);
        }
      }

      .chat-widget-toggle.pulse {
        animation: pulse 2s infinite;
      }
    `;
    document.head.appendChild(style);
  }

  setupEventListeners() {
    const toggle = document.getElementById('chatWidgetToggle');
    // Renamed from `window` to avoid shadowing the global `window` object
    const widgetWindow = document.getElementById('chatWidgetWindow');
    if (!toggle || !widgetWindow) return;

    toggle.addEventListener('click', () => {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        toggle.classList.add('active');
        toggle.classList.remove('pulse');
        widgetWindow.classList.add('open');
      } else {
        toggle.classList.remove('active');
        widgetWindow.classList.remove('open');
      }
    });

    // Close widget when clicking outside
    document.addEventListener('click', (e) => {
      const widget = document.getElementById('chatWidget');
      if (this.isOpen && widget && !widget.contains(e.target)) {
        this.isOpen = false;
        toggle.classList.remove('active');
        widgetWindow.classList.remove('open');
      }
    });

    // Optional: Add pulse animation on first load to draw attention
    setTimeout(() => {
      toggle.classList.add('pulse');
    }, 1000);
  }
}

// Initialize widget when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ChatWidget();
  });
} else {
  new ChatWidget();
}
